import { Box, Divider, IconButton } from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon, MenuIcon, MenuUpIcon } from '@atgfr8/icons';
import { tryGetRoute } from '@components/Sidebar/helpers';
import { A } from '@solidjs/router';
import { For, Show, createSignal, onCleanup, onMount } from 'solid-js';

import { NavLink } from './NavLink';

type NavOverflowMenuProps = {
  navlinks: NavLink[];
};

export default function NavOverflowMenu(props: NavOverflowMenuProps) {
  const [buttonEl, setButtonEl] = createSignal<HTMLButtonElement>();
  const [menuEl, setMenuEl] = createSignal<HTMLDivElement>();
  const [isOpen, setIsOpen] = createSignal(false);
  const [expandedId, setExpandedId] = createSignal<string | null>(null);

  const handleClose = () => {
    setIsOpen(false);
    setExpandedId(null);
  };

  const handleToggle = () => {
    if (isOpen()) {
      handleClose();
    } else {
      setIsOpen(true);
    }
  };

  const handleSectionClick = (navlink: NavLink) => {
    if (navlink.custom) {
      navlink.custom();
      handleClose();
      return;
    }

    if (!navlink.children?.length) {
      handleClose();
      return;
    }

    setExpandedId((current) => (current === navlink.id ? null : navlink.id));
  };

  const handleChildClick = (custom?: () => void) => {
    custom?.();
    handleClose();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!isOpen()) {
      return;
    }

    const target = event.target as Node;
    if (buttonEl()?.contains(target) || menuEl()?.contains(target)) {
      return;
    }

    handleClose();
  };

  onMount(() => {
    document.addEventListener('click', handleClickOutside, true);
  });

  onCleanup(() => {
    document.removeEventListener('click', handleClickOutside, true);
  });

  return (
    <div class="relative flex items-center shrink-0 px-1">
      <IconButton
        aria-label="More navigation"
        aria-haspopup="true"
        aria-expanded={isOpen() ? 'true' : undefined}
        ref={setButtonEl}
        onClick={() => handleToggle()}
        class="text-[#026EA1]! bg-[#026EA1]/20! hover:bg-[#026EA1]/35! focus:bg-[#026EA1]/35! border border-[#026EA1]/50! rounded-md! px-2! py-1!"
        dataTestId="nav-overflow-menu-button"
      >
        <MenuIcon />
      </IconButton>
      <Show when={isOpen()}>
        <Box
          ref={setMenuEl}
          class="absolute top-[40px] right-0 bg-white z-53 text-[#000000DE] py-1 rounded-md shadow-[0px_4px_10px_rgba(0,0,0,0.47)] text-left min-w-[220px]"
        >
          <For each={props.navlinks}>
            {(navlink) => {
              const hasChildren = () => Boolean(navlink.children?.length);
              const isExpanded = () => expandedId() === navlink.id;

              return (
                <div>
                  <button
                    type="button"
                    class="w-full flex items-center justify-between gap-3 whitespace-nowrap text-[#000000DE] hover:bg-[#0e2c3b] hover:text-white text-[14px] font-semibold px-[16px] py-[8px] text-left cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSectionClick(navlink);
                    }}
                    data-testid={`nav-overflow-section-${navlink.id}`}
                  >
                    <span>{navlink.label}</span>
                    <Show when={hasChildren()}>
                      <Show
                        when={isExpanded()}
                        fallback={<MenuDownIcon size={18} />}
                      >
                        <MenuUpIcon size={18} />
                      </Show>
                    </Show>
                  </button>
                  <Show when={hasChildren() && isExpanded()}>
                    <For
                      each={navlink.children?.filter(
                        (child) => !child.hide?.(),
                      )}
                    >
                      {(child) => {
                        const route = tryGetRoute(child.path);
                        return (
                          <>
                            <div
                              class="whitespace-nowrap text-[#000000DE] hover:bg-[#0e2c3b] hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChildClick(child.custom);
                              }}
                            >
                              {child.mailto !== undefined && child.mailto ? (
                                <a
                                  class="capitalize block w-full whitespace-nowrap text-[14px] font-normal px-[28px] py-[6px]"
                                  target={'_blank'}
                                  href={child.path}
                                  onClick={(e) => {
                                    handleChildClick(child.custom);
                                    e.stopPropagation();
                                  }}
                                >
                                  {child.label}
                                </a>
                              ) : Boolean(child.externalLink) || route.isV3 ? (
                                <A
                                  href={
                                    Boolean(child.externalLink)
                                      ? child.path
                                      : route.route
                                  }
                                  target={
                                    Boolean(child.externalLink) || route.isV3
                                      ? '_blank'
                                      : undefined
                                  }
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleChildClick(child.custom);
                                    window.open(
                                      Boolean(child.externalLink)
                                        ? child.path
                                        : route.route,
                                      '_blank',
                                    );
                                    e.stopPropagation();
                                  }}
                                  class="capitalize block w-full whitespace-nowrap text-[14px] font-normal px-[28px] py-[6px]"
                                >
                                  {child.label}
                                </A>
                              ) : (
                                <A
                                  href={
                                    Boolean(child.externalLink)
                                      ? child.path
                                      : route.route
                                  }
                                  target={
                                    Boolean(child.externalLink)
                                      ? '_blank'
                                      : undefined
                                  }
                                  class="capitalize block w-full whitespace-nowrap text-[14px] font-normal px-[28px] py-[6px]"
                                  onClick={(e) => {
                                    handleChildClick(child.custom);
                                    e.stopPropagation();
                                  }}
                                >
                                  {child.label}
                                </A>
                              )}
                            </div>
                            {child.seperator === true ? (
                              <Divider class="my-2!" />
                            ) : null}
                          </>
                        );
                      }}
                    </For>
                  </Show>
                </div>
              );
            }}
          </For>
        </Box>
      </Show>
    </div>
  );
}
